import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useDismissOnBlockingOverlayOpen } from '@/shared/hooks/useDismissOnBlockingOverlayOpen';
import { cn } from '@/shared/utils/cn';

interface PdpCartFullPopupProps {
  open: boolean;
  cartCount: number;
  maxCartItemsCount: number;
  isWishlisted: boolean;
  hasLocalOverlayOpen?: boolean;
  onClose: () => void;
  onAddToWishlist: () => void;
}

export function PdpCartFullPopup({
  open,
  cartCount,
  maxCartItemsCount,
  isWishlisted,
  hasLocalOverlayOpen = false,
  onClose,
  onAddToWishlist,
}: PdpCartFullPopupProps) {
  const isMobile = useIsMobile();
  const displayCount = Math.max(cartCount, maxCartItemsCount);

  useDismissOnBlockingOverlayOpen({
    isOpen: open,
    onDismiss: onClose,
    hasLocalOverlayOpen,
  });

  if (!open) return null;

  const handleAddToWishlist = () => {
    onAddToWishlist();
    onClose();
  };

  return (
    <div
      data-pdp-cart-full-popup="true"
      className={cn(
        // Common styles
        'fixed z-[1200] rounded-xl shadow-[0_4px_20px_rgba(0,0,0,0.15)]',
        // Background: rgb(244, 250, 255)
        'bg-[#f4faff]',
        // Mobile: full-width above BuyNowBar + chat icon
        isMobile
          ? 'bottom-[124px] left-3 right-3'
          // Desktop: fixed bottom-right above chat icon
          : 'bottom-[124px] right-4 w-[360px]'
      )}
    >
      {/* Header */}
      <div className="relative p-5 pb-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-6 w-6 items-center justify-center text-gray-500 hover:text-gray-700"
          aria-label="Close"
        >
          <span className="text-lg leading-none">&times;</span>
        </button>

        <p className="pr-6 text-[15px] font-bold leading-snug text-[#252525]">
          Your cart is full ({displayCount} items)
        </p>
        <p className="mt-1.5 text-[13px] leading-snug text-[#555]">
          Please remove item or add additional items to your wishlist.
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 px-5 py-4">
        {!isWishlisted && (
          <button
            type="button"
            onClick={handleAddToWishlist}
            className="rounded bg-[#1a3a5c] px-4 py-2 text-[13px] font-semibold text-white"
          >
            Add to wishlist
          </button>
        )}
        <a href="/order" className="text-[13px] font-semibold text-[#1a3a5c]">
          View Cart
        </a>
      </div>
    </div>
  );
}
